import { useState, useMemo } from "react";
import { Link as WLink } from "wouter";
import {
  useListCompensationNeedsReview,
  useGetCompensationCoverage,
  useGetContractCompensation,
  useUpdateContractCompensation,
  useBackfillContractCompensation,
  useBackfillAllCompensation,
  useBulkConfirmCompensation,
  getGetContractCompensationQueryKey,
  getListCompensationNeedsReviewQueryKey,
  getGetCompensationCoverageQueryKey,
  type ContractCompensation,
  type CompStatus,
  type PaymentCadence,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { useOrg } from "@/lib/orgContext";
import { Loader2, CheckCircle2, AlertCircle, RefreshCw, ChevronRight } from "lucide-react";

const STATUS_VARIANT: Record<CompStatus, { label: string; className: string }> = {
  confirmed: { label: "Confirmed", className: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  proposed: { label: "Needs review", className: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
  no_value: { label: "No value", className: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
};

function StatusBadge({ status }: { status: CompStatus }) {
  const v = STATUS_VARIANT[status];
  return <Badge className={v.className} variant="secondary">{v.label}</Badge>;
}

function formatMoney(value: string | null): string {
  if (!value) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

interface FieldEditorProps {
  label: string;
  value: string | null;
  status: CompStatus;
  source: string;
  excerpt: string | null;
  onConfirm: (value: string) => void;
  onMarkNoValue: () => void;
  saving: boolean;
}

function MoneyFieldRow({ label, value, status, source, excerpt, onConfirm, onMarkNoValue, saving }: FieldEditorProps) {
  const [draft, setDraft] = useState<string>(value ?? "");
  return (
    <div className="rounded-md border p-3 space-y-2" data-testid={`field-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="font-medium">{label}</div>
        <StatusBadge status={status} />
      </div>
      {excerpt && (
        <div className="text-xs text-muted-foreground italic border-l-2 pl-2">"{excerpt}"</div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="0.00"
          className="max-w-[200px]"
        />
        <span className="text-xs text-muted-foreground">Extractor said: {formatMoney(value)}</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" onClick={onMarkNoValue} disabled={saving} data-testid={`no-value-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            No value
          </Button>
          <Button size="sm" onClick={() => onConfirm(draft)} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
          </Button>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground">Source: {source}</div>
    </div>
  );
}

interface BuyoutDraftEntry {
  effectiveDate: string;
  amount: string;
}

function BuyoutEditor({
  status,
  entries,
  onSave,
  onMarkNoValue,
  saving,
}: {
  status: CompStatus;
  entries: Array<{ id: number; effectiveDate: string; amount: string; source: string }>;
  onSave: (entries: BuyoutDraftEntry[]) => void;
  onMarkNoValue: () => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<BuyoutDraftEntry[]>(() =>
    entries.map((e) => ({ effectiveDate: e.effectiveDate, amount: e.amount })),
  );

  return (
    <div className="rounded-md border p-3 space-y-2" data-testid="field-buyout">
      <div className="flex items-center justify-between">
        <div className="font-medium">Buyout schedule</div>
        <StatusBadge status={status} />
      </div>
      {!editing ? (
        <>
          {entries.length === 0 ? (
            <div className="text-sm text-muted-foreground">No buyout entries detected.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground"><th>Effective date</th><th>Amount</th><th>Source</th></tr></thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>{e.effectiveDate}</td>
                    <td>{formatMoney(e.amount)}</td>
                    <td className="text-xs text-muted-foreground">{e.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={onMarkNoValue} disabled={saving} data-testid="no-value-buyout">No value</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={saving}>Edit</Button>
            {status === "proposed" && entries.length > 0 && (
              <Button size="sm" onClick={() => onSave(entries.map((e) => ({ effectiveDate: e.effectiveDate, amount: e.amount })))} disabled={saving} data-testid="confirm-buyout">
                Confirm as-is
              </Button>
            )}
          </div>
        </>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th>Effective date (YYYY-MM-DD)</th><th>Amount</th><th></th></tr></thead>
            <tbody>
              {draft.map((e, i) => (
                <tr key={i}>
                  <td><Input value={e.effectiveDate} placeholder="2025-01-01" onChange={(ev) => setDraft((d) => d.map((x, j) => j === i ? { ...x, effectiveDate: ev.target.value } : x))} className="max-w-[160px]" /></td>
                  <td><Input type="number" min="0" step="0.01" value={e.amount} onChange={(ev) => setDraft((d) => d.map((x, j) => j === i ? { ...x, amount: ev.target.value } : x))} className="max-w-[160px]" /></td>
                  <td><Button size="sm" variant="ghost" onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}>Remove</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={() => setDraft((d) => [...d, { effectiveDate: "", amount: "" }])}>Add row</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(entries.map((e) => ({ effectiveDate: e.effectiveDate, amount: e.amount }))); }}>Cancel</Button>
            <Button size="sm" onClick={() => { onSave(draft); setEditing(false); }} disabled={saving}>Save & confirm</Button>
          </div>
        </>
      )}
    </div>
  );
}

function CompensationDetail({ contractId }: { contractId: number }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useGetContractCompensation(contractId);
  const update = useUpdateContractCompensation();
  const backfill = useBackfillContractCompensation();
  const comp = data as ContractCompensation | undefined;
  const [cadenceDraft, setCadenceDraft] = useState<PaymentCadence | "">("");

  if (isLoading || !comp) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: getGetContractCompensationQueryKey(contractId) }),
      qc.invalidateQueries({ queryKey: getListCompensationNeedsReviewQueryKey() }),
      qc.invalidateQueries({ queryKey: getGetCompensationCoverageQueryKey() }),
    ]);

  async function confirmField(field: "baseSalary" | "guaranteedBonus" | "incentiveCap", value: string) {
    try {
      await update.mutateAsync({
        id: contractId,
        data: { [field]: { value: value === "" ? null : Number(value), status: "confirmed" } } as Record<string, unknown>,
      });
      await invalidate();
      toast({ title: "Saved", description: `${field} confirmed.` });
    } catch (e) {
      handleApiError(toast, e);
    }
  }

  async function markFieldNoValue(field: "baseSalary" | "guaranteedBonus" | "incentiveCap" | "paymentCadence" | "buyoutSchedule") {
    try {
      const patch: Record<string, unknown> =
        field === "buyoutSchedule"
          ? { buyoutSchedule: { status: "no_value", entries: [] } }
          : field === "paymentCadence"
            ? { paymentCadence: { value: null, status: "no_value" } }
            : { [field]: { value: null, status: "no_value" } };
      await update.mutateAsync({ id: contractId, data: patch });
      await invalidate();
      toast({ title: "Marked no value", description: `${field} skipped.` });
    } catch (e) {
      handleApiError(toast, e);
    }
  }

  async function saveBuyout(entries: BuyoutDraftEntry[]) {
    try {
      const cleaned = entries
        .filter((e) => e.effectiveDate && e.amount !== "")
        .map((e) => ({ effectiveDate: e.effectiveDate, amount: Number(e.amount) }));
      await update.mutateAsync({
        id: contractId,
        data: { buyoutSchedule: { status: "confirmed", entries: cleaned } } as Record<string, unknown>,
      });
      await invalidate();
      toast({ title: "Saved", description: "Buyout schedule confirmed." });
    } catch (e) {
      handleApiError(toast, e);
    }
  }

  async function confirmCadence() {
    if (!cadenceDraft) return;
    try {
      await update.mutateAsync({
        id: contractId,
        data: { paymentCadence: { value: cadenceDraft, status: "confirmed" } } as Record<string, unknown>,
      });
      await invalidate();
      toast({ title: "Saved", description: "Payment cadence confirmed." });
    } catch (e) {
      handleApiError(toast, e);
    }
  }

  async function rerunBackfill() {
    try {
      await backfill.mutateAsync({ id: contractId });
      await invalidate();
      toast({ title: "Backfill complete", description: "Extractor re-ran. Confirmed fields preserved." });
    } catch (e) {
      handleApiError(toast, e);
    }
  }

  const cadence = comp.paymentCadence;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Last extracted: {comp.lastBackfilledAt ? new Date(comp.lastBackfilledAt).toLocaleString() : "never"}
        </div>
        <Button variant="outline" size="sm" onClick={rerunBackfill} disabled={backfill.isPending}>
          <RefreshCw className="h-4 w-4 mr-1" /> Re-run extractor
        </Button>
      </div>
      <MoneyFieldRow
        label="Base salary"
        value={comp.baseSalary.value}
        status={comp.baseSalary.status}
        source={comp.baseSalary.source}
        excerpt={comp.baseSalary.excerpt}
        onConfirm={(v) => confirmField("baseSalary", v)}
        onMarkNoValue={() => markFieldNoValue("baseSalary")}
        saving={update.isPending}
      />
      <MoneyFieldRow
        label="Guaranteed bonus"
        value={comp.guaranteedBonus.value}
        status={comp.guaranteedBonus.status}
        source={comp.guaranteedBonus.source}
        excerpt={comp.guaranteedBonus.excerpt}
        onConfirm={(v) => confirmField("guaranteedBonus", v)}
        onMarkNoValue={() => markFieldNoValue("guaranteedBonus")}
        saving={update.isPending}
      />
      <MoneyFieldRow
        label="Incentive cap"
        value={comp.incentiveCap.value}
        status={comp.incentiveCap.status}
        source={comp.incentiveCap.source}
        excerpt={comp.incentiveCap.excerpt}
        onConfirm={(v) => confirmField("incentiveCap", v)}
        onMarkNoValue={() => markFieldNoValue("incentiveCap")}
        saving={update.isPending}
      />
      <div className="rounded-md border p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-medium">Payment cadence</div>
          <StatusBadge status={cadence.status} />
        </div>
        {cadence.excerpt && <div className="text-xs italic border-l-2 pl-2">"{cadence.excerpt}"</div>}
        <div className="flex items-center gap-2">
          <Select value={cadenceDraft || (cadence.value ?? "")} onValueChange={(v) => setCadenceDraft(v as PaymentCadence)}>
            <SelectTrigger className="max-w-[220px]"><SelectValue placeholder="Select cadence" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="semi_monthly">Semi-monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">Extractor said: {cadence.value ?? "—"}</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => markFieldNoValue("paymentCadence")} disabled={update.isPending} data-testid="no-value-cadence">
              No value
            </Button>
            <Button size="sm" onClick={confirmCadence} disabled={update.isPending}>
              Confirm
            </Button>
          </div>
        </div>
      </div>
      <BuyoutEditor
        status={comp.buyoutSchedule.status}
        entries={comp.buyoutSchedule.entries}
        onSave={saveBuyout}
        onMarkNoValue={() => markFieldNoValue("buyoutSchedule")}
        saving={update.isPending}
      />
    </div>
  );
}

export default function CompensationVerificationPage() {
  const { org } = useOrg();
  const personas = org?.personas ?? [];
  const isAdmin = org?.role === "admin";
  const isFinance = personas.includes("Finance");
  const canEdit = isAdmin || isFinance;

  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: needsReview, isLoading: loadingList } = useListCompensationNeedsReview();
  const { data: coverage } = useGetCompensationCoverage();
  const backfillAll = useBackfillAllCompensation();
  const bulkConfirm = useBulkConfirmCompensation();

  const [selectedId, setSelectedId] = useState<number | null>(null);

  const items = useMemo(
    () => (needsReview as { items?: Array<{ contractId: number; coachName: string; sport: string | null; statuses: Record<string, CompStatus> }> })?.items ?? [],
    [needsReview],
  );

  const totals = (coverage as { totals?: { total: number; fullyConfirmed: number; anyProposed: number; allNoValue: number } } | undefined)?.totals;
  const bySport = (coverage as { bySport?: Array<{ sport: string; total: number; fullyConfirmed: number; anyProposed: number; allNoValue: number }> } | undefined)?.bySport ?? [];

  if (!canEdit) {
    return (
      <div className="p-6 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>Compensation verification</CardTitle>
            <CardDescription>This page is available to org admins and the Finance persona.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  async function runBulkConfirm() {
    if (!confirm("Mark every extractor-proposed compensation field as confirmed across all contracts? This cannot be undone in bulk.")) return;
    try {
      const r = await bulkConfirm.mutateAsync();
      const data = r as { contractsUpdated: number };
      await qc.invalidateQueries({ queryKey: getListCompensationNeedsReviewQueryKey() });
      await qc.invalidateQueries({ queryKey: getGetCompensationCoverageQueryKey() });
      toast({ title: "Bulk confirm complete", description: `${data.contractsUpdated} contracts updated.` });
    } catch (e) {
      handleApiError(toast, e);
    }
  }

  async function runBackfillAll() {
    try {
      const r = await backfillAll.mutateAsync();
      const data = r as { processed: number; createdRows: number };
      await qc.invalidateQueries({ queryKey: getListCompensationNeedsReviewQueryKey() });
      await qc.invalidateQueries({ queryKey: getGetCompensationCoverageQueryKey() });
      toast({ title: "Backfill complete", description: `${data.processed} contracts processed, ${data.createdRows} new rows.` });
    } catch (e) {
      handleApiError(toast, e);
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Compensation verification</h1>
          <p className="text-sm text-muted-foreground">Review extractor output and confirm structured comp data for budgeting.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runBulkConfirm} disabled={bulkConfirm.isPending} data-testid="run-bulk-confirm">
            {bulkConfirm.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
            Confirm all proposed
          </Button>
          <Button onClick={runBackfillAll} disabled={backfillAll.isPending} data-testid="run-backfill-all">
            {backfillAll.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Backfill all contracts
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Coverage</CardTitle>
          <CardDescription>Confirmed fields unlock downstream Finance reports.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="rounded border p-3"><div className="text-xs text-muted-foreground">Contracts</div><div className="text-2xl font-semibold">{totals?.total ?? 0}</div></div>
            <div className="rounded border p-3"><div className="text-xs text-muted-foreground">Fully confirmed</div><div className="text-2xl font-semibold text-emerald-600">{totals?.fullyConfirmed ?? 0}</div></div>
            <div className="rounded border p-3"><div className="text-xs text-muted-foreground">Needs review</div><div className="text-2xl font-semibold text-amber-600">{totals?.anyProposed ?? 0}</div></div>
            <div className="rounded border p-3"><div className="text-xs text-muted-foreground">All no-value</div><div className="text-2xl font-semibold text-slate-500">{totals?.allNoValue ?? 0}</div></div>
          </div>
          <Separator />
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-muted-foreground border-b">
                <th className="py-1">Sport</th><th>Total</th><th>Confirmed</th><th>Needs review</th><th>No value</th>
              </tr></thead>
              <tbody>
                {bySport.map((r) => (
                  <tr key={r.sport} className="border-b last:border-0">
                    <td className="py-1">{r.sport}</td>
                    <td>{r.total}</td>
                    <td className="text-emerald-600">{r.fullyConfirmed}</td>
                    <td className="text-amber-600">{r.anyProposed}</td>
                    <td className="text-slate-500">{r.allNoValue}</td>
                  </tr>
                ))}
                {bySport.length === 0 && <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">No contracts yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Needs review ({items.length})</CardTitle>
            <CardDescription>Contracts with at least one extractor-proposed field.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-auto divide-y">
              {loadingList && <div className="p-4"><Loader2 className="h-4 w-4 animate-spin" /></div>}
              {!loadingList && items.length === 0 && (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                  All contracts have confirmed compensation data.
                </div>
              )}
              {items.map((it) => {
                const proposedCount = Object.values(it.statuses).filter((s) => s === "proposed").length;
                return (
                  <button
                    key={it.contractId}
                    type="button"
                    onClick={() => setSelectedId(it.contractId)}
                    className={`w-full text-left p-3 hover:bg-muted/40 flex items-center gap-2 ${selectedId === it.contractId ? "bg-muted/60" : ""}`}
                    data-testid={`needs-review-${it.contractId}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{it.coachName}</div>
                      <div className="text-xs text-muted-foreground">{it.sport ?? "—"} · contract #{it.contractId}</div>
                    </div>
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      <AlertCircle className="h-3 w-3 mr-1" /> {proposedCount}
                    </Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{selectedId ? `Contract #${selectedId}` : "Select a contract"}</CardTitle>
            {selectedId && (
              <CardDescription>
                <WLink href={`/contracts/${selectedId}`} className="text-primary underline">Open full contract →</WLink>
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {selectedId ? <CompensationDetail contractId={selectedId} /> : <div className="text-sm text-muted-foreground">Choose a contract from the list to verify its compensation fields.</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
